import React, { useState, useMemo } from 'react';
import { Modal, TextInput, ScrollView } from 'react-native';
import {
  StackScreen,
  Flex,
  PrimaryButton,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useRegisterPoc } from '../queries';
import { bridge, isWeb } from 'shared/src/utils';
import Toast from 'react-native-toast-message';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export const AddContactModal = ({ visible, onClose }: Props) => {
  const { width } = useScreenSize();
  const insets = useSafeAreaInsets();
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [mobile, setMobile] = useState('');

  /** 전화번호 유효성 */
  const isPhoneValid = useMemo(() => mobile.length >= 10, [mobile]);

  const isFormValid = useMemo(() => {
    return name.trim() && relation.trim() && isPhoneValid;
  }, [name, relation, isPhoneValid]);

  const { mutateAsync: registerPoc } = useRegisterPoc();

  const resetState = () => {
    setName('');
    setRelation('');
    setMobile('');
  };

  const handleComplete = async () => {
    if (!isFormValid) return;

    try {
      await registerPoc({ name, relation, mobile });
      if (!isWeb) {
        bridge.toast('상담 그룹 정보가 등록되었어요');
      } else {
        window.confirm('상담 그룹 정보가 등록되었어요');
      }

      handleClose();
      resetState();
    } catch (ex) {
      const error = ex as Error;
      Toast.show({
        type: 'error',
        text1: error.message,
        position: 'bottom',
      });
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="none"
      statusBarTranslucent
      transparent
      onRequestClose={handleClose}
    >
      <StackScreen
        title="상담 그룹 추가하기"
        onPressBack={handleClose}
        bottom={{
          element: (
            <Flex
              padding={{ top: 16, horizontal: 16, bottom: insets.bottom + 16 }}
            >
              <PrimaryButton
                variant="fill"
                color="pink"
                size="xlarge"
                state={isFormValid ? undefined : 'disabled'}
                onPress={handleComplete}
              >
                동의하고 저장하기
              </PrimaryButton>
            </Flex>
          ),
          avoidKeyboard: true,
          additionalKeyboardOffset: 0,
        }}
      >
        <ScrollView
          contentContainerStyle={{
            width,
            marginHorizontal: 'auto',
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <Flex padding={{ vertical: 16, horizontal: 16 }} gap={24}>
            {/* 이름 입력 */}
            <Flex gap={16}>
              <Flex.Row gap={8} alignItems="center">
                <Typography type="subtitle1" color="content_primary">
                  이름
                </Typography>
                <Flex
                  width={6}
                  height={6}
                  borderRadius={3}
                  backgroundColor="bg_pink_primary"
                />
              </Flex.Row>

              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="이름을 입력해주세요"
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 12,
                  fontSize: 14,
                  borderWidth: 1,
                  borderColor: '#E6E6E6',
                  borderRadius: 3,
                  color: '#111',
                }}
              />
            </Flex>

            {/* 관계 입력 */}
            <Flex gap={16}>
              <Flex.Row gap={8} alignItems="center">
                <Typography type="subtitle1" color="content_primary">
                  관계
                </Typography>
                <Flex
                  width={6}
                  height={6}
                  borderRadius={3}
                  backgroundColor="bg_pink_primary"
                />
              </Flex.Row>

              <TextInput
                value={relation}
                onChangeText={setRelation}
                placeholder="예) 어머니, 아버지, 친구"
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 12,
                  fontSize: 14,
                  borderWidth: 1,
                  borderColor: '#E6E6E6',
                  borderRadius: 3,
                  color: '#111',
                }}
              />
            </Flex>

            {/* 휴대폰 번호 입력 */}
            <Flex gap={16}>
              <Flex.Row gap={8} alignItems="center">
                <Typography type="subtitle1" color="content_primary">
                  휴대폰 번호
                </Typography>
                <Flex
                  width={6}
                  height={6}
                  borderRadius={3}
                  backgroundColor="bg_pink_primary"
                />
              </Flex.Row>

              <TextInput
                value={mobile}
                onChangeText={text =>
                  setMobile(text.replace(/\D/g, '').slice(0, 11))
                }
                placeholder="(-)를 제외한 숫자만 입력해주세요"
                keyboardType="number-pad"
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 12,
                  fontSize: 14,
                  borderWidth: 1,
                  borderColor: '#E6E6E6',
                  borderRadius: 3,
                  color: '#111',
                }}
              />
            </Flex>
          </Flex>

          {/* 개인정보 제공 동의 안내 */}
          <Flex
            backgroundColor="gray10"
            padding={{ vertical: 12, horizontal: 16 }}
            gap={16}
          >
            <Typography type="subtitle1" color="content_primary">
              개인정보 제공 동의 안내
            </Typography>
            <Typography type="body2" color="content_secondary">
              회원이 상담 그룹 서비스를 이용하고자 할 경우, 에이블리는 원활한
              서비스 제공을 위하여 필요한 최소한의 개인정보를 아래와 같이
              제공하고 있습니다.
              {'\n'}
              {'\n'}
              <Typography type="subtitle2" color="content_secondary">
                ① 개인정보를 제공받는 자: 상담 그룹으로 등록된 자
              </Typography>
              {'\n'}
              {'\n'}
              <Typography type="subtitle2" color="content_secondary">
                ② 개인정보를 제공받는 자의 개인정보 이용 목적: 회원의 주문 관련
                사항 확인 및 고객 상담
              </Typography>
              {'\n'}
              {'\n'}③ 제공하는 개인정보의 항목: 상품 구매 정보, 결제 수단,
              수령인 정보 등 회원의 주문 관련 일체의 정보 및 회원과의 관계 확인을
              위한 회원 계정 정보
              {'\n'}
              {'\n'}④ 개인정보를 제공받는 자의 개인정보 보유 및 이용{' '}
              <Typography type="subtitle2" color="content_secondary">
                기간: 개인정보 이용 목적 달성 시까지 보존. 단, 관계 법령의 규정에
                의하여 일정 기간 보존이 필요한 경우에는 해당 기간만큼 보관 후 삭제
              </Typography>
              {'\n'}
              {'\n'}
              회원은 위 동의를 거부할 권리가 있으나, 거부 시에는 상담 그룹
              서비스 이용이 불가능합니다.
              {'\n'}
              {'\n'}그 밖의 내용은 에이블리 서비스 이용약관 및
              개인정보처리방침에 따릅니다.
            </Typography>
          </Flex>
        </ScrollView>
      </StackScreen>
      <Toast />
    </Modal>
  );
};
